import * as React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    IconButton,
    Popover,
    Stack,
    Typography,
} from '@mui/material';

export interface FieldInfoContent {
    title: string;
    /** What this control does. */
    what: string;
    /** Concrete examples; shown as a bullet list. */
    examples?: string[];
    /** Optional “watch out” note. */
    tip?: string;
}

/** Small (i) that opens a popover with explanation + examples. */
export default function FieldInfoButton(props: { info: FieldInfoContent; 'aria-label'?: string }) {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchor);

    return (
        <>
            <IconButton
                size="small"
                color="primary"
                aria-label={props['aria-label'] ?? `Info: ${props.info.title}`}
                onClick={event => {
                    event.stopPropagation();
                    setAnchor(event.currentTarget);
                }}
                sx={{ p: 0.35 }}
            >
                <InfoOutlinedIcon fontSize="small" />
            </IconButton>
            <Popover
                open={open}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ sx: { maxWidth: 420, p: 2 } }}
            >
                <Stack gap={1.25} onClick={event => event.stopPropagation()}>
                    <Typography variant="subtitle2">{props.info.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {props.info.what}
                    </Typography>
                    {props.info.examples && props.info.examples.length > 0 && (
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                                Examples
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {props.info.examples.map(example => (
                                    <Typography key={example} component="li" variant="body2" sx={{ mb: 0.5 }}>
                                        {example}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {props.info.tip && (
                        <Typography variant="body2" sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                            <strong>Tip:</strong> {props.info.tip}
                        </Typography>
                    )}
                </Stack>
            </Popover>
        </>
    );
}

export function LabelWithInfo(props: {
    label: React.ReactNode;
    info: FieldInfoContent;
}) {
    return (
        <Stack direction="row" alignItems="center" gap={0.5} component="span" sx={{ display: 'inline-flex' }}>
            <span>{props.label}</span>
            <FieldInfoButton info={props.info} />
        </Stack>
    );
}
